import i18n from '@/libs/i18n'

/**
 * The refusals this portal can actually meet, and nothing else.
 *
 * It is `pms-web/src/libs/error.ts` and `pms-housekeeping`'s copy of it, cut to the
 * codes a person can produce about THEMSELVES: a stamp that does not follow the day, a
 * month that has been signed off, a request for leave that overlaps another. Every
 * refusal about a folio, a till or somebody else's room stayed behind — no screen here
 * can reach one, and a case that can never fire is a case nobody maintains.
 *
 * The numbers are `pms-api/pkg/enums/error`. They are written out rather than imported
 * because there is nothing to import them from, and a number that drifts from the
 * service is the fault this list is most exposed to — so each one names its sentinel.
 */
export enum Errors {
    InternalServerError = 1,
    InvalidData         = 2,
    NotFound            = 3,
    Unauthorized        = 4,
    FailedToInsert      = 5,
    ItemAlreadyExists   = 6,
    Forbidden           = 7,
    IncorrectStatus     = 8,

    /**
     * The same press is already being carried out. Never taken over automatically —
     * the first attempt may already have written the stamp.
     */
    OperationInProgress = 11,

    /** One operation name arriving with different contents: the screen is stale. */
    OperationMismatch   = 12,

    /**
     * A route of a module this hotel has not bought.
     *
     * It shares 403 with Forbidden and is not the same thing: Forbidden is "this
     * account may not", which another right can fix, and this is "this house has not
     * got the module", which nobody signed in can fix at all (docs/MODULI.md §3.2).
     * The whole of this portal belongs to `staff`, so meeting this once means there is
     * no portal — libs/license.ts and components/ModuleNotice.vue.
     */
    ModuleNotLicensed   = 19,

    /**
     * A stamp from somebody with no personnel card in force today (docs/OSEBJE.md §5).
     *
     * The one refusal here that is about the RECORD rather than the person: a
     * seasonal worker in February, an account nobody has written a card for. It has
     * its own sentence because "you have no right" would send somebody to their
     * supervisor asking for a permission that would not help.
     */
    StaffNotEmployed    = 39,

    /** A stamp that cannot follow the day as it stands — a departure with no arrival (§9). */
    StaffTimeSequence      = 40,

    /** A stamp dated outside the window the record will take. */
    StaffTimeOutOfRange    = 41,

    /** A break stamp in a house that does not record breaks (§8). */
    StaffBreakRecordingOff = 42,

    /**
     * Another press for this person is between reading the day and writing to it
     * (docs/EVIDENCA-CASA.md §8). 409, and its own code because the answer is „press
     * it again“ rather than „you are already clocked in“ — which matters most on this
     * portal, where the queue retries by itself and a wrong-state sentence would take
     * a perfectly good stamp off it.
     */
    StaffTimeBusy          = 62,

    /**
     * A write into a month somebody has signed off: a correction, an absence, a stamp
     * on a past day inside it (docs/EVIDENCA-CASA.md § „Zaključevanje meseca“).
     *
     * Its own sentence because most of the people who meet it cannot take it —
     * reopening is `staff:manage`, which nobody reading this portal holds — so the
     * only useful thing to say is what happened and who to ask.
     */
    StaffTimeMonthClosed   = 64,
}

/**
 * The API's own code, for a caller that has to DO something rather than print a
 * different sentence. Zero when the body carries none.
 */
export const parseErrorCode = (errorObject: unknown): number =>
    (errorObject as { error?: { code?: number } })?.error?.code ?? 0

/**
 * Turns the API's `{"error": {"code": n, "message": …}}` body into a sentence.
 *
 * Reads through `i18n.global` so it works outside a setup context — `libs/staffTime.ts`
 * calls it from a queue that is draining on a timer with no component around it.
 *
 * # The server's own words win by default
 *
 * The account is in `pms-web/src/libs/error.ts` and is not repeated: a refusal whose
 * whole point is the WHY used to arrive as „Napaka pri urejanju“. So `body.message` is
 * read first, a caller's fallback is used only when the server sent nothing, and the
 * generic sentence is the last resort it was meant to be.
 *
 * The codes below that answer a fixed TRANSLATED sentence regardless of the fallback are
 * the exception, and every one of them has the same reason: the service's message is
 * right and it is in ENGLISH, and this portal is read on a telephone by a cook, a
 * laundress and a housekeeper. Where the sentence has to be acted on rather than merely
 * read, it has to be in their own language.
 */
export const parseError = (errorObject: unknown, fallback = ''): string => {
    const t = i18n.global.t
    const raw = (errorObject as { error?: unknown })?.error

    // A bare-string body (PrintHTTP.WriteErrorMessage: {"error": "..."}) carries no
    // code for anything below to key on.
    if (typeof raw === 'string') return raw || fallback || t('errors.generic_error')

    const body = raw as { code?: number, message?: string } | undefined
    const code = body?.code

    if (!code) return fallback || t('errors.generic_error')

    switch (code) {
        case Errors.InvalidData:       return t('errors.invalid_data')
        case Errors.NotFound:          return t('errors.not_found')
        case Errors.Unauthorized:      return t('errors.unauthorized')
        case Errors.Forbidden:         return t('errors.unauthorized')
        case Errors.ItemAlreadyExists: return body?.message || fallback || t('errors.conflict')
        case Errors.IncorrectStatus:   return body?.message || fallback || t('errors.generic_error')
        case Errors.OperationInProgress: return t('errors.operation_in_progress')
        case Errors.OperationMismatch:   return t('errors.operation_mismatch')
        // Said plainly rather than as "you have no right": nobody here can be given
        // this one. The full-screen notice is what the person actually reads — this is
        // for a call that answered before it.
        case Errors.ModuleNotLicensed: return t('errors.module_not_licensed')
        // Said as what it is — the card, not the person.
        case Errors.StaffNotEmployed:  return t('errors.staff_not_employed')
        case Errors.StaffTimeSequence:      return t('errors.staff_time_sequence')
        case Errors.StaffTimeOutOfRange:    return t('errors.staff_time_out_of_range')
        case Errors.StaffBreakRecordingOff: return t('errors.staff_break_recording_off')
        // „Poskusite znova“ and nothing else. It is the one refusal here that means
        // the press was FINE — see the enum.
        case Errors.StaffTimeBusy:          return t('errors.staff_time_busy')
        // The month is shut. The sentence has to carry who can open it, because the
        // person reading it cannot.
        case Errors.StaffTimeMonthClosed:   return t('errors.staff_time_month_closed')
        default:                       return body?.message || fallback || t('errors.generic_error')
    }
}
